import { cloneElement } from 'react';
import type { ReactElement } from 'react';
import { ComPeriodo, acharCartao } from '../../design/graficos/ComPeriodo';
import { agrupar } from '../../design/graficos/agrupar';
import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import {
  MODELOS_COMPARACAO,
  MODELOS_EMPILHADO,
  MODELOS_META,
  MODELOS_PARTES,
  MODELOS_QUANDO,
  MODELOS_TEMPO,
  MODELOS_VELAS,
  compararPessoas,
  contraMeta,
  empilhado,
  fmt,
  partes,
  quandoVende,
  serieNoTempo,
  velas,
} from '../../design/graficos/modelos';
import type { GaleriaDeGraficos as Dados, Periodo } from '../../paineis';

/**
 * A GALERIA DE GRÁFICOS — organizada por pergunta, cada gráfico com o número, a conclusão e a
 * troca de modelo. Usada na seção Gráficos (a loja inteira) e na ficha do balconista (só ele):
 * com `pessoaId`, todo número é SÓ daquela pessoa e as comparações são com o período anterior dela.
 *
 * Toda frase é calculada dos números reais do período. Sem dado: o cartão diz que não há.
 */

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const PROGRAMAS = [
  { id: 'vitamina' as const, nome: 'Vitamina', cor: '#1a7f55' },
  { id: 'pre_vencido' as const, nome: 'Pré-vencido', cor: '#cb1b2b' },
  { id: 'paradao' as const, nome: 'Paradão', cor: '#b7791f' },
];
const NOME_DO_PROGRAMA: Record<string, string> = {
  vitamina: 'Vitamina',
  pre_vencido: 'Pré-vencido',
  paradao: 'Paradão',
};
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const soma = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
const variacao = (atual: number, anterior: number) =>
  anterior > 0 ? ((atual - anterior) / anterior) * 100 : null;
const primeiroNome = (s: string) => {
  const p = s.trim().split(/\s+/)[0] ?? s;
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
};

type Secao = 'metas' | 'evolucao' | 'composicao' | 'equipe' | 'quando' | 'produtos';

/** Todos os cartões para UM conjunto de dados (um período). O invólucro escolhe qual mostrar. */
function arvore(dados: Dados, secoes?: readonly Secao[]): ReactElement {
  const ver = (s: NonNullable<typeof secoes>[number]) => !secoes || secoes.includes(s);
  const quem = dados.pessoa ? primeiroNome(dados.pessoa.nome) : 'a loja';
  const d = dados.diario;
  const a = dados.diarioAnterior;
  const blocos = agrupar(d);
  const blocosAnt = agrupar(a);
  const cats = blocos.map((x) => x.rotulo);
  const fat = soma(d.map((x) => x.faturamento));
  const fatAnt = soma(a.map((x) => x.faturamento));
  const cupons = soma(d.map((x) => x.cupons));
  const cuponsAnt = soma(a.map((x) => x.cupons));
  const itens = soma(d.map((x) => x.itens));
  const ticket = cupons ? fat / cupons : null;
  const ticketAnt = cuponsAnt ? fatAnt / cuponsAnt : null;
  const melhorDia = [...d].sort((x, y) => y.faturamento - x.faturamento)[0];
  const semVenda = cupons === 0;
  const fonteHos = 'Fonte: vendas do HOS (API, automática)';
  const fonteInova = 'Fonte: lançamentos no InovaPay';

  // programas (lançados no InovaPay)
  const porPrograma = PROGRAMAS.map((p) => ({
    ...p,
    itens: soma(dados.programas.filter((x) => x.programa === p.id).map((x) => x.itens)),
    premio: soma(dados.programas.filter((x) => x.programa === p.id).map((x) => x.premio)),
  }));
  const premioTotal = soma(porPrograma.map((p) => p.premio));
  const liderPrograma = [...porPrograma].sort((x, y) => y.premio - x.premio)[0];

  // quando vende
  const pico = [...dados.porHora].sort((x, y) => y.cupons - x.cupons)[0];
  const picoPct = pico && cupons ? (pico.cupons / cupons) * 100 : null;
  const porDow = Array.from({ length: 7 }, (_, dow) =>
    soma(dados.mapaDeCalor.filter((x) => x.dow === dow).map((x) => x.cupons)),
  );
  const melhorDow = porDow.indexOf(Math.max(...porDow));

  // meta
  const alvo = dados.meta.itensPorPessoa;
  const bateram = dados.meta.pessoas.filter((p) => p.itens >= alvo).length;
  const pctBateram = dados.meta.pessoas.length ? (bateram / dados.meta.pessoas.length) * 100 : 0;
  const itensDaPessoa = dados.meta.pessoas[0]?.itens ?? 0;

  // equipe
  const equipe = dados.porBalconista;
  const lider = equipe[0];
  const top5 = soma(equipe.slice(0, 5).map((p) => p.faturamento));
  const concentracao = fat && equipe.length > 5 ? (top5 / fat) * 100 : null;

  // velas
  const velasDias = dados.velas;
  const maiorAmplitude = [...velasDias].sort(
    (x, y) => y.maximo - y.minimo - (x.maximo - x.minimo),
  )[0];

  return (
    <div className="gx-galeria">
      {ver('metas') && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>Metas</h2>
            <p>
              Meta semanal de itens dos programas · semana {diaCurto(dados.meta.semana.de)} a{' '}
              {diaCurto(dados.meta.semana.ate)} (sábado a sexta)
            </p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="meta"
              titulo={dados.pessoa ? 'Minha meta da semana' : 'Quem bateu a meta'}
              numero={
                dados.pessoa
                  ? `${String(itensDaPessoa)} de ${String(alvo)} itens`
                  : dados.meta.pessoas.length
                    ? `${String(bateram)} de ${String(dados.meta.pessoas.length)}`
                    : 'sem lançamento'
              }
              conclusao={
                dados.pessoa ? (
                  itensDaPessoa >= alvo ? (
                    <>
                      <strong>Meta batida</strong>: {String(itensDaPessoa)} itens dos programas na
                      semana, {String(itensDaPessoa - alvo)} acima da meta.
                    </>
                  ) : (
                    <>
                      Faltaram <strong>{String(alvo - itensDaPessoa)} itens</strong> para a meta de{' '}
                      {String(alvo)} nesta semana.
                    </>
                  )
                ) : (
                  <>
                    <strong>{fmt(pctBateram, 'pct')}</strong> da equipe bateu a meta de{' '}
                    {String(alvo)} itens. A linha preta marca a meta; verde bateu, amarelo chegou
                    perto (70%+), vermelho ficou longe.
                  </>
                )
              }
              modelos={
                dados.pessoa ? MODELOS_META.filter(([m]) => m !== 'progresso') : MODELOS_META
              }
              padrao={dados.pessoa ? 'velocimetro' : 'bullet'}
              desenhar={(m) =>
                contraMeta(m, {
                  itens: dados.meta.pessoas.map((p) => ({
                    nome: primeiroNome(p.nome),
                    valor: p.itens,
                  })),
                  alvo,
                  unidade: 'itens',
                  resumo: dados.pessoa
                    ? { nome: 'da meta', pct: alvo ? (itensDaPessoa / alvo) * 100 : 0 }
                    : { nome: 'bateram a meta', pct: pctBateram },
                })
              }
              fonte={fonteInova}
              vazio={
                dados.meta.pessoas.length === 0
                  ? 'Nenhum item de programa lançado nesta semana.'
                  : null
              }
              altura={dados.pessoa ? 260 : Math.max(220, dados.meta.pessoas.length * 26 + 50)}
            />
            <CartaoDeGrafico
              id="funil"
              titulo="Do lançamento ao pagamento"
              numero={fmt(dados.funil.lancados, 'inteiro')}
              conclusao={
                <>
                  De <strong>{fmt(dados.funil.lancados, 'inteiro')}</strong> vendas lançadas no
                  período, {fmt(dados.funil.ativos, 'inteiro')} seguem valendo e{' '}
                  <strong>{fmt(dados.funil.emSemanaPaga, 'inteiro')}</strong> já estão em semana
                  paga.
                </>
              }
              modelos={MODELOS_PARTES.filter(
                ([m]) => m === 'funil' || m === 'barras' || m === 'colunas',
              )}
              padrao="funil"
              desenhar={(m) =>
                partes(m, {
                  itens: [
                    { nome: 'Lançadas', valor: dados.funil.lancados },
                    { nome: 'Valendo', valor: dados.funil.ativos },
                    { nome: 'Em semana paga', valor: dados.funil.emSemanaPaga },
                  ],
                  formato: 'inteiro',
                })
              }
              fonte={fonteInova}
              vazio={dados.funil.lancados === 0 ? 'Nenhuma venda lançada no período.' : null}
              altura={240}
            />
          </div>
        </section>
      )}

      {ver('evolucao') && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>Como {dados.pessoa ? 'está indo' : 'a loja está indo'}</h2>
            <p>
              {diaCurto(dados.periodo.de)} a {diaCurto(dados.periodo.ate)}, comparado com{' '}
              {diaCurto(dados.anterior.de)} a {diaCurto(dados.anterior.ate)} (tracejado)
            </p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="faturamento"
              largo
              titulo={dados.pessoa ? `Vendas de ${quem}` : 'Faturamento'}
              numero={fmt(fat, 'reais')}
              variacao={{ pct: variacao(fat, fatAnt), bom: 'subir' }}
              conclusao={
                semVenda ? (
                  'Nenhuma venda no período.'
                ) : (
                  <>
                    {quem.charAt(0).toUpperCase() + quem.slice(1)} vendeu{' '}
                    <strong>{fmt(fat, 'reais')}</strong> em {fmt(cupons, 'inteiro')} cupons
                    {fatAnt ? (
                      <>
                        , {fat >= fatAnt ? 'acima' : 'abaixo'} do período anterior (
                        {fmt(fatAnt, 'reais')})
                      </>
                    ) : null}
                    . Melhor dia:{' '}
                    <strong>
                      {melhorDia
                        ? `${diaCurto(melhorDia.dia)} (${fmt(melhorDia.faturamento, 'reais')})`
                        : '—'}
                    </strong>
                    .
                  </>
                )
              }
              modelos={MODELOS_TEMPO}
              padrao="area"
              desenhar={(m) =>
                serieNoTempo(m, {
                  cats,
                  atual: blocos.map((x) => x.faturamento),
                  anterior: blocosAnt.map((x) => x.faturamento),
                  nome: 'Faturamento',
                  formato: 'reais',
                  linha: {
                    nome: 'Ticket médio',
                    valores: blocos.map((x) => x.ticket),
                    formato: 'reais',
                  },
                })
              }
              fonte={fonteHos}
              vazio={semVenda ? 'Nenhuma venda do HOS no período.' : null}
              altura={300}
            />
            <CartaoDeGrafico
              id="ticket"
              titulo="Ticket médio"
              numero={fmt(ticket, 'reais')}
              variacao={{
                pct: ticket !== null && ticketAnt !== null ? variacao(ticket, ticketAnt) : null,
                bom: 'subir',
              }}
              conclusao={
                <>
                  Cada cliente deixou em média <strong>{fmt(ticket, 'reais')}</strong> por cupom
                  {ticketAnt ? <> (antes: {fmt(ticketAnt, 'reais')})</> : null}.
                </>
              }
              modelos={MODELOS_TEMPO.filter(([m]) => m !== 'combo')}
              padrao="linha"
              desenhar={(m) =>
                serieNoTempo(m, {
                  cats,
                  atual: blocos.map((x) => x.ticket),
                  anterior: blocosAnt.map((x) => x.ticket),
                  nome: 'Ticket médio',
                  formato: 'reais',
                })
              }
              fonte={fonteHos}
              vazio={semVenda ? 'Nenhuma venda do HOS no período.' : null}
            />
            <CartaoDeGrafico
              id="cupons"
              titulo="Clientes atendidos (cupons)"
              numero={fmt(cupons, 'inteiro')}
              variacao={{ pct: variacao(cupons, cuponsAnt), bom: 'subir' }}
              conclusao={
                <>
                  <strong>{fmt(cupons, 'inteiro')}</strong> cupons, {fmt(itens, 'inteiro')} itens:{' '}
                  <strong>{fmt(cupons ? itens / cupons : null, 'decimal')}</strong> itens por cupom.
                </>
              }
              modelos={MODELOS_TEMPO.filter(([m]) => m !== 'combo')}
              padrao="colunas"
              desenhar={(m) =>
                serieNoTempo(m, {
                  cats,
                  atual: blocos.map((x) => x.cupons),
                  anterior: blocosAnt.map((x) => x.cupons),
                  nome: 'Cupons',
                  formato: 'inteiro',
                })
              }
              fonte={fonteHos}
              vazio={semVenda ? 'Nenhuma venda do HOS no período.' : null}
            />
          </div>
        </section>
      )}

      {ver('composicao') && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>Programas de premiação</h2>
            <p>Vitamina, pré-vencido e paradão lançados no InovaPay no período</p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="premio-programa"
              titulo="Premiação por programa"
              numero={fmt(premioTotal, 'reais')}
              conclusao={
                premioTotal ? (
                  <>
                    <strong>{liderPrograma?.nome}</strong> gerou a maior parte:{' '}
                    {fmt(liderPrograma ? (liderPrograma.premio / premioTotal) * 100 : 0, 'pct')} da
                    premiação do período.
                  </>
                ) : (
                  'Nenhuma premiação lançada no período.'
                )
              }
              modelos={MODELOS_PARTES}
              padrao="rosca"
              desenhar={(m) =>
                partes(m, {
                  itens: porPrograma
                    .filter((p) => p.premio > 0)
                    .map((p) => ({ nome: p.nome, valor: p.premio, cor: p.cor })),
                  formato: 'reais',
                })
              }
              fonte={fonteInova}
              vazio={premioTotal ? null : 'Nenhuma premiação lançada no período.'}
            />
            <CartaoDeGrafico
              id="programa-dia"
              titulo="Itens por programa, dia a dia"
              numero={`${fmt(soma(porPrograma.map((p) => p.itens)), 'inteiro')} itens`}
              conclusao={
                <>
                  {porPrograma.map((p, i) => (
                    <span key={p.id}>
                      {i ? ' · ' : ''}
                      {p.nome} <strong>{fmt(p.itens, 'inteiro')}</strong>
                    </span>
                  ))}
                  . Passe o mouse num dia para ver a divisão.
                </>
              }
              modelos={MODELOS_EMPILHADO}
              padrao="colunas_empilhadas"
              desenhar={(m) =>
                empilhado(m, {
                  cats,
                  formato: 'inteiro',
                  series: PROGRAMAS.map((p) => ({
                    nome: p.nome,
                    cor: p.cor,
                    valores: blocos.map((b) =>
                      soma(
                        dados.programas
                          .filter((y) => b.dias.includes(y.dia) && y.programa === p.id)
                          .map((y) => y.itens),
                      ),
                    ),
                  })),
                })
              }
              fonte={fonteInova}
              vazio={dados.programas.length ? null : 'Nenhum item de programa lançado no período.'}
            />
            {!dados.pessoa && (
              <CartaoDeGrafico
                id="venn"
                titulo="Quem vende cada programa"
                numero={`${fmt(soma(dados.venn.map((v) => v.pessoas)), 'inteiro')} pessoas`}
                conclusao={
                  <>
                    Combinações de programas que cada pessoa vendeu no período.{' '}
                    <strong>
                      {fmt(
                        dados.venn.find((v) => v.programas.length === 3)?.pessoas ?? 0,
                        'inteiro',
                      )}
                    </strong>{' '}
                    venderam os três.
                  </>
                }
                modelos={MODELOS_PARTES.filter(([m]) => m !== 'funil')}
                padrao="barras"
                desenhar={(m) =>
                  partes(m, {
                    itens: dados.venn.map((v) => ({
                      nome: v.programas.map((p) => NOME_DO_PROGRAMA[p] ?? p).join(' + '),
                      valor: v.pessoas,
                    })),
                    formato: 'inteiro',
                  })
                }
                fonte={fonteInova}
                vazio={dados.venn.length ? null : 'Ninguém lançou venda de programa no período.'}
                altura={240}
              />
            )}
          </div>
        </section>
      )}

      {ver('equipe') && !dados.pessoa && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>A equipe</h2>
            <p>Vendas do HOS por balconista (operador do cupom) no período</p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="ranking-vendas"
              titulo="Quem mais vendeu"
              numero={
                lider ? `${primeiroNome(lider.nome)} · ${fmt(lider.faturamento, 'reais')}` : '—'
              }
              conclusao={
                concentracao !== null ? (
                  <>
                    Os 5 que mais venderam concentram <strong>{fmt(concentracao, 'pct')}</strong> do
                    faturamento.
                  </>
                ) : (
                  'Faturamento de cada balconista no período.'
                )
              }
              modelos={MODELOS_PARTES}
              padrao="barras"
              desenhar={(m) =>
                partes(m, {
                  itens: equipe
                    .slice(0, 15)
                    .map((p) => ({ nome: primeiroNome(p.nome), valor: p.faturamento })),
                  formato: 'reais',
                })
              }
              fonte={fonteHos}
              vazio={equipe.length ? null : 'Nenhuma venda do HOS no período.'}
              altura={Math.max(260, Math.min(15, equipe.length) * 24 + 40)}
            />
            <CartaoDeGrafico
              id="comparar-equipe"
              titulo="Como cada um vende"
              numero={`${String(equipe.length)} balconistas`}
              conclusao={
                <>
                  Cupons, ticket médio, itens e produtos diferentes por cupom de cada balconista.
                  Maior ticket (com 30+ cupons):{' '}
                  <strong>
                    {(() => {
                      const t = [...equipe]
                        .filter((p) => p.cupons >= 30)
                        .sort((x, y) => y.ticket - x.ticket)[0];
                      return t ? `${primeiroNome(t.nome)} (${fmt(t.ticket, 'reais')})` : '—';
                    })()}
                  </strong>
                  .
                </>
              }
              modelos={MODELOS_COMPARACAO}
              padrao="bolhas"
              desenhar={(m) => compararPessoas(m, equipe, null)}
              fonte={fonteHos}
              vazio={equipe.length ? null : 'Nenhuma venda do HOS no período.'}
              altura={320}
            />
          </div>
        </section>
      )}

      {ver('quando') && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>Quando vende</h2>
            <p>Dia da semana e hora dos cupons do período</p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="calor"
              largo
              titulo="Movimento por dia e hora"
              numero={pico ? `pico às ${String(pico.hora)}h` : '—'}
              conclusao={
                pico ? (
                  <>
                    O horário mais forte é <strong>{String(pico.hora)}h</strong> (
                    {fmt(picoPct, 'pct')} dos cupons), e o dia mais forte é{' '}
                    <strong>{DIAS[melhorDow] ?? '—'}</strong>.
                  </>
                ) : (
                  'Nenhuma venda no período.'
                )
              }
              modelos={MODELOS_QUANDO}
              padrao="calor"
              desenhar={(m) => quandoVende(m, { mapa: dados.mapaDeCalor, porHora: dados.porHora })}
              fonte={fonteHos}
              vazio={semVenda ? 'Nenhuma venda do HOS no período.' : null}
              altura={300}
            />
            <CartaoDeGrafico
              id="velas"
              largo
              titulo="Ticket ao longo do dia (velas)"
              numero={maiorAmplitude ? `maior variação em ${diaCurto(maiorAmplitude.dia)}` : '—'}
              conclusao={
                <>
                  Como o ticket médio varia dentro de cada dia: de onde abriu (primeira hora com
                  venda) a onde fechou (última hora), e a hora de ticket mais baixo e mais alto. Nas
                  velas, verde = fechou acima de onde abriu.
                </>
              }
              modelos={MODELOS_VELAS}
              padrao="velas"
              desenhar={(m) =>
                velas(m, { cats: velasDias.map((x) => diaCurto(x.dia)), dados: velasDias })
              }
              fonte={fonteHos}
              vazio={velasDias.length ? null : 'Nenhuma venda do HOS no período.'}
              altura={280}
            />
          </div>
        </section>
      )}

      {ver('produtos') && (
        <section className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>Produtos</h2>
            <p>Os 10 produtos que mais faturaram no período</p>
          </div>
          <div className="gx-grade">
            <CartaoDeGrafico
              id="top-produtos"
              largo
              titulo="Top 10 produtos"
              numero={dados.topProdutos[0] ? fmt(dados.topProdutos[0].faturamento, 'reais') : '—'}
              conclusao={
                dados.topProdutos[0] ? (
                  <>
                    <strong>{dados.topProdutos[0].nome}</strong> lidera com{' '}
                    {fmt(dados.topProdutos[0].quantidade, 'inteiro')} unidades. Os 10 somam{' '}
                    <strong>
                      {fmt(
                        fat ? (soma(dados.topProdutos.map((p) => p.faturamento)) / fat) * 100 : 0,
                        'pct',
                      )}
                    </strong>{' '}
                    do faturamento.
                  </>
                ) : (
                  'Nenhuma venda no período.'
                )
              }
              modelos={MODELOS_PARTES}
              padrao="barras"
              desenhar={(m) =>
                partes(m, {
                  itens: dados.topProdutos.map((p) => ({ nome: p.nome, valor: p.faturamento })),
                  formato: 'reais',
                })
              }
              fonte={fonteHos}
              vazio={dados.topProdutos.length ? null : 'Nenhuma venda do HOS no período.'}
              altura={320}
            />
          </div>
        </section>
      )}
    </div>
  );
}

const SECOES: readonly { id: Secao; titulo: string; sub: string; cartoes: readonly string[] }[] = [
  {
    id: 'metas',
    titulo: 'Metas',
    sub: 'Meta semanal de itens dos programas e o caminho do lançamento ao pagamento',
    cartoes: ['meta', 'funil'],
  },
  {
    id: 'evolucao',
    titulo: 'Como está indo',
    sub: 'Faturamento, ticket e clientes contra o período anterior (tracejado)',
    cartoes: ['faturamento', 'ticket', 'cupons'],
  },
  {
    id: 'composicao',
    titulo: 'Programas de premiação',
    sub: 'Vitamina, pré-vencido e paradão lançados no InovaPay',
    cartoes: ['premio-programa', 'programa-dia', 'venn'],
  },
  {
    id: 'equipe',
    titulo: 'A equipe',
    sub: 'Vendas do HOS por balconista (operador do cupom)',
    cartoes: ['ranking-vendas', 'comparar-equipe'],
  },
  {
    id: 'quando',
    titulo: 'Quando vende',
    sub: 'Dia da semana e hora dos cupons',
    cartoes: ['calor', 'velas'],
  },
  {
    id: 'produtos',
    titulo: 'Produtos',
    sub: 'Os 10 produtos que mais faturaram',
    cartoes: ['top-produtos'],
  },
];
const LARGOS = new Set(['faturamento', 'calor', 'velas', 'top-produtos']);

/**
 * A galeria: cada cartão tem o SEU período (Página, Hoje, Semana, Mês, Trimestre, Ano ou livre) —
 * "Página" segue o período do topo da tela. Cartões com o mesmo período dividem uma busca só.
 */
export function GaleriaDeGraficos({
  periodo,
  pessoaId = null,
  secoes,
}: {
  periodo: Periodo;
  pessoaId?: string | null;
  /** Só estas seções (padrão: todas). */
  secoes?: readonly Secao[];
}) {
  return (
    <div className="gx-galeria">
      {SECOES.filter((x) => !secoes || secoes.includes(x.id)).map((sec) => (
        <section key={sec.id} className="gx-secao">
          <div className="gx-secao-cabeca">
            <h2>{sec.titulo}</h2>
            <p>{sec.sub}</p>
          </div>
          <div className="gx-grade">
            {sec.cartoes.map((id) => (
              <ComPeriodo
                key={id}
                id={`galeria:${id}${pessoaId ? ':pessoa' : ''}`}
                padrao={periodo}
                pessoaId={pessoaId}
              >
                {(g, seletor, carregando) => {
                  if (!g)
                    return (
                      <div
                        className={`gx-cartao gx-carregando${LARGOS.has(id) ? ' gx-largo' : ''}`}
                      >
                        {seletor}
                        {carregando
                          ? 'Carregando…'
                          : 'Não foi possível carregar este gráfico agora.'}
                      </div>
                    );
                  const c = acharCartao(arvore(g, secoes), id);
                  return c ? cloneElement(c, { periodo: seletor }) : null;
                }}
              </ComPeriodo>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
